// @ts-strict-ignore
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { uniqueId } from 'lodash-es';
import { of, Subject, switchMap } from 'rxjs';
import { delay } from 'rxjs/operators';
import { ANIMATION_DURATION } from '../../../../design-system/design-system.constants';
import { UI_STRINGS } from '../../../../functions/language/uistrings';
import { Language } from '../../../../model/enums/language';
import { LabelIconData } from '../label-icon/label-icon.component.types';

@Component({
  selector: 'app-expansion-panel',
  templateUrl: './expansion-panel.component.html',
  styleUrls: ['./expansion-panel.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ExpansionPanelComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() clipContent: boolean = true;
  @Input() contentBorder: boolean = true;
  @Input() contentPadding: boolean = true;
  @Input() disabled: boolean = false;
  @Input() header: string;
  @Input() headerIcon: LabelIconData;
  @Input() headerWarningIcon: LabelIconData;
  /**
   * temporary until we have handling of heading levels.
   */
  @Input() headingLevel: 'h2' | 'h3' | 'h4' = 'h3';
  @Input() lang: Language;
  @Input() style: { [klass: string]: any } = {};
  @Input() toggleable: boolean = true;
  @Input() selected?: boolean = false;

  @Output() isExpanded = new EventEmitter<boolean>();

  @ViewChild('contentWrapper') contentWrapper: ElementRef<HTMLDivElement>;

  buttonId = uniqueId('accordion-');
  expanded: boolean;
  isOpen: boolean = false;

  private _accordionChange$ = new Subject<boolean>();

  readonly GLOBAL_STRINGS = UI_STRINGS.GLOBAL;

  constructor(private _cdr: ChangeDetectorRef) {
    this._accordionChange$
      .pipe(switchMap((expanded) => of(expanded).pipe(delay(ANIMATION_DURATION))))
      .subscribe((expanded) => {
        this.expanded = expanded;
        if (expanded) {
          this.contentWrapper.nativeElement.style.maxHeight = 'none';
        } else {
          this.contentWrapper.nativeElement.style.visibility = 'hidden';
        }
        this._cdr.markForCheck();
      });
  }

  ngOnInit(): void {
    this.isOpen = this.selected;
    this.isExpanded.emit(this.isOpen);
  }

  ngAfterViewInit(): void {
    if (!this.isOpen) {
      this.contentWrapper.nativeElement.style.visibility = 'hidden';
    }
  }

  ngOnDestroy() {
    this._accordionChange$.unsubscribe();
  }

  onExpandedChange(expanded: boolean) {
    // Work around for focus state, see open issue: https://github.com/primefaces/primeng/issues/11821
    this._animateExpanded(expanded);
    this.isOpen = expanded;
    this.isExpanded.emit(expanded);
  }

  onToggle() {
    this.isOpen = !this.isOpen;
    this.onExpandedChange(this.isOpen);
  }

  private _animateExpanded(expanded: boolean) {
    if (this.contentWrapper?.nativeElement) {
      if (expanded) {
        // When expanding, start with 0 height and make visible
        this.contentWrapper.nativeElement.style.maxHeight = '0';
        this.contentWrapper.nativeElement.style.visibility = 'visible';
        // Force a reflow to ensure the style change takes effect
        this.contentWrapper.nativeElement.offsetHeight;
        // Then set to full height for animation
        const currentHeight = this.contentWrapper.nativeElement.scrollHeight;
        this.contentWrapper.nativeElement.style.maxHeight = `${currentHeight}px`;
      } else {
        // When collapsing, capture current height first
        const currentHeight = this.contentWrapper.nativeElement.scrollHeight;
        this.contentWrapper.nativeElement.style.maxHeight = `${currentHeight}px`;
        // Force a reflow to ensure the style change takes effect
        this.contentWrapper.nativeElement.offsetHeight;
        // Then animate to 0
        this.contentWrapper.nativeElement.style.maxHeight = '0';
      }
      this._accordionChange$.next(expanded);
    }
  }
}
